import { CloseIcon, PlusIcon } from '@/assets/icons'
import { iconSizes } from '@/theme/tokens'
import React, { useId } from 'react'
import {
	GroupHint,
	GroupMarker,
	SectionBody,
	SectionHead,
	SectionText,
	SectionTitle,
	SectionToggle,
} from './EventCreateFlow.styles'

interface ExtrasSectionProps {
	title: string
	subtitle: string
	/** Open once there's something in it; its body then brings its own way to add more. */
	expanded: boolean
	/** A section whose plus turns into a ✕ that clears and closes it once open (notes). */
	removable?: boolean
	onAdd: () => void
	onRemove?: () => void
	children: React.ReactNode
}

/**
 * One Extras section: its title (every extra is optional, and says so) and
 * subtitle with a trailing plus, then a body that shows once there's something
 * in it. The plus fades once the body shows — or, for a removable section,
 * becomes the ✕ that closes it.
 */
const ExtrasSection: React.FC<ExtrasSectionProps> = ({
	title,
	subtitle,
	expanded,
	removable = false,
	onAdd,
	onRemove,
	children,
}) => {
	const headingId = useId()
	const closes = removable && expanded
	const hidesToggle = expanded && !removable

	return (
		<section aria-labelledby={headingId}>
			<SectionHead>
				<SectionText>
					<SectionTitle id={headingId}>
						{title}
						<GroupMarker>(optional)</GroupMarker>
					</SectionTitle>
					<GroupHint>{subtitle}</GroupHint>
				</SectionText>
				<SectionToggle
					type="button"
					$hidden={hidesToggle}
					$closes={closes}
					aria-hidden={hidesToggle || undefined}
					tabIndex={hidesToggle ? -1 : undefined}
					aria-label={`${closes ? 'Remove' : 'Add'} ${title.toLowerCase()}`}
					onClick={closes ? onRemove : onAdd}
				>
					{closes ? <CloseIcon sx={{ fontSize: iconSizes.lg }} /> : <PlusIcon sx={{ fontSize: iconSizes.lg }} />}
				</SectionToggle>
			</SectionHead>
			{expanded && <SectionBody>{children}</SectionBody>}
		</section>
	)
}

export default ExtrasSection
